const STATUSES = ["Cancelled", "Closed", "On-hold", "Open", "Pending", "Solved"];
const PRIORITY_OPTIONS = ["Critical", "High", "Low", "Medium"];

/**
 * Preset date-range options shown in the "Created Date" filter dropdown.
 * The "custom" entry reveals explicit from/to date pickers below the row.
 */
const DATE_PRESETS = [
    { value: "7d",         label: "Last 7 days" },
    { value: "30d",        label: "Last 30 days" },
    { value: "90d",        label: "Last 90 days" },
    { value: "this_month", label: "This month" },
    { value: "last_month", label: "Last month" },
    { value: "custom",     label: "Custom range…" },
];

/**
 * FilterPanel Component
 * Renders the advanced filters panel for action items.
 * Provides filtering by source/type, status, priority, property, assignee, creator,
 * created-date range, and closed items visibility.
 *
 * @param {Object}   props
 * @param {string}   props.filterSource               - Current source/type filter value
 * @param {Function} props.onFilterSourceChange        - Handler for source/type change
 * @param {string}   props.filterStatus               - Current status filter value
 * @param {Function} props.onFilterStatusChange        - Handler for status change
 * @param {string}   props.filterPriority             - Current priority filter value
 * @param {Function} props.onFilterPriorityChange      - Handler for priority change
 * @param {string}   props.filterProperty             - Current property filter value
 * @param {Function} props.onFilterPropertyChange      - Handler for property change
 * @param {string}   props.filterAssignee             - Current assignee filter value
 * @param {Function} props.onFilterAssigneeChange      - Handler for assignee change
 * @param {string}   props.filterCreator              - Current creator filter value
 * @param {Function} props.onFilterCreatorChange       - Handler for creator change
 * @param {boolean}  props.showClosedItems            - Whether to show closed items
 * @param {Function} props.onShowClosedItemsChange     - Handler for show-closed toggle
 * @param {Function} props.onClearFilters              - Clears all filters
 * @param {Array}    props.availableSourceOptions      - Dynamic source options [{value, label}]
 * @param {Array}    props.propertiesMDA               - Properties for the property dropdown
 * @param {Array}    props.allUsers                    - Users for assignee/creator dropdowns
 * @param {Function} props.getUserDisplayName          - Returns display name for a user object
 * @param {number}   props.filteredCount              - Count of currently visible items
 * @param {number}   props.totalCount                 - Total count of all loaded items
 * @param {string}   props.filterDatePreset           - Active preset key (e.g. "last_month")
 * @param {Function} props.onFilterDatePresetChange    - Handler for preset change (receives value string)
 * @param {string}   props.filterDateFrom             - ISO date string for custom range start (YYYY-MM-DD)
 * @param {Function} props.onFilterDateFromChange      - Handler for custom from-date (receives value string)
 * @param {string}   props.filterDateTo               - ISO date string for custom range end (YYYY-MM-DD)
 * @param {Function} props.onFilterDateToChange        - Handler for custom to-date (receives value string)
 */
const FilterPanel = ({
    filterSource,
    onFilterSourceChange,
    filterStatus,
    onFilterStatusChange,
    filterPriority,
    onFilterPriorityChange,
    filterProperty,
    onFilterPropertyChange,
    filterAssignee,
    onFilterAssigneeChange,
    filterCreator,
    onFilterCreatorChange,
    showClosedItems,
    onShowClosedItemsChange,
    onClearFilters,
    availableSourceOptions = [],
    propertiesMDA,
    allUsers,
    getUserDisplayName,
    filteredCount,
    totalCount,
    filterDatePreset = "",
    onFilterDatePresetChange,
    filterDateFrom = "",
    onFilterDateFromChange,
    filterDateTo = "",
    onFilterDateToChange,
}) => {
    return (
        <div className="filter-panel-container">
            {/* ── Main Filters Row ── */}
            <div className="advanced-filters">
                <div className="filter-group">
                    <label>Source/Type</label>
                    {/* Dynamic source options derived from the actual loaded action items.
                        This list automatically includes all source types present in the
                        data (e.g., RouterMonitoring, Project Tasks, template-based types)
                        and updates when the "Show Closed Items" toggle is changed. */}
                    <select
                        value={filterSource}
                        onChange={onFilterSourceChange}
                    >
                        <option value="">All Sources</option>
                        {availableSourceOptions.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="filter-group">
                    <label>Status</label>
                    <select
                        value={filterStatus}
                        onChange={onFilterStatusChange}
                    >
                        <option value="">All Statuses</option>
                        {STATUSES.map(s => (
                            <option key={s} value={s}>{s}</option>
                        ))}
                    </select>
                </div>

                <div className="filter-group">
                    <label>Priority</label>
                    <select
                        value={filterPriority}
                        onChange={onFilterPriorityChange}
                    >
                        <option value="">All Priorities</option>
                        {PRIORITY_OPTIONS.map(p => (
                            <option key={p} value={p}>{p}</option>
                        ))}
                    </select>
                </div>

                <div className="filter-group">
                    <label>Property</label>
                    <select
                        value={filterProperty}
                        onChange={onFilterPropertyChange}
                    >
                        <option value="">All Properties</option>
                        {[...propertiesMDA]
                            .sort((a, b) => a.propertyName.localeCompare(b.propertyName))
                            .map(p => (
                                <option key={p.propertyID} value={p.propertyID}>
                                    {p.propertyName}
                                </option>
                            ))}
                    </select>
                </div>

                <div className="filter-group">
                    <label>Assignee</label>
                    <select
                        value={filterAssignee}
                        onChange={onFilterAssigneeChange}
                    >
                        <option value="">All Assignees</option>
                        <option value="me_or_observer_or_dept_or_created">My Items (Assigned, Observing, Dept, or Created)</option>
                        <option value="me_or_observer_or_dept">Assigned to me, Observing, or My Department</option>
                        <option value="me_or_observer">Assigned to me or Observing</option>
                        <option value="me">Assigned to me</option>
                        <option value="created_by_me">Created by me</option>
                        <option value="observing">Observing</option>
                        <option value="my_department">My Department</option>
                        <option value="collaborating">Collaborating</option>
                        <option value="unassigned">Unassigned</option>
                        {[...allUsers]
                            .sort((a, b) => {
                                const nameA = getUserDisplayName(a) || `User ${a.userID || a.id}`;
                                const nameB = getUserDisplayName(b) || `User ${b.userID || b.id}`;
                                return nameA.localeCompare(nameB);
                            })
                            .map(u => {
                                const userId = u.userID || u.id;
                                const displayName = getUserDisplayName(u) || `User ${userId}`;
                                return (
                                    <option key={userId} value={userId}>
                                        {displayName}
                                    </option>
                                );
                            })}
                    </select>
                </div>

                <div className="filter-group">
                    <label>Creator</label>
                    <select
                        value={filterCreator}
                        onChange={onFilterCreatorChange}
                    >
                        <option value="">All Creators</option>
                        <option value="created_by_me">Created by me</option>
                        {[...allUsers]
                            .sort((a, b) => {
                                const nameA = getUserDisplayName(a) || `User ${a.userID || a.id}`;
                                const nameB = getUserDisplayName(b) || `User ${b.userID || b.id}`;
                                return nameA.localeCompare(nameB);
                            })
                            .map(u => {
                                const userId = u.userID || u.id;
                                const displayName = getUserDisplayName(u) || `User ${userId}`;
                                return (
                                    <option key={userId} value={userId}>
                                        {displayName}
                                    </option>
                                );
                            })}
                    </select>
                </div>

                {/* ── Created Date preset filter ── */}
                <div className="filter-group">
                    <label>Created Date</label>
                    <select
                        value={filterDatePreset}
                        onChange={e => onFilterDatePresetChange(e.target.value)}
                    >
                        <option value="">Any date</option>
                        {DATE_PRESETS.map(p => (
                            <option key={p.value} value={p.value}>{p.label}</option>
                        ))}
                    </select>
                </div>

                <div className="filter-tail">
                    <div className="filter-group filter-checkbox">
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                checked={showClosedItems}
                                onChange={onShowClosedItemsChange}
                            />
                            <span>Show Closed Items</span>
                        </label>
                    </div>

                    <div className="filter-actions">
                        <button
                            type="button"
                            className="btn-clear"
                            onClick={onClearFilters}
                        >
                            Clear All
                        </button>
                    </div>
                </div>
            </div>

            {/* ── Custom date range pickers — only shown when "Custom range…" is selected ── */}
            {filterDatePreset === "custom" && (
                <div className="advanced-filters filter-custom-date-row">
                    <div className="filter-group">
                        <label>From Date</label>
                        <input
                            type="date"
                            value={filterDateFrom}
                            onChange={e => onFilterDateFromChange(e.target.value)}
                            max={filterDateTo || undefined}
                        />
                    </div>
                    <div className="filter-group">
                        <label>To Date</label>
                        <input
                            type="date"
                            value={filterDateTo}
                            onChange={e => onFilterDateToChange(e.target.value)}
                            min={filterDateFrom || undefined}
                        />
                    </div>
                </div>
            )}

            {/* Results Count */}
            <div className="results-count">
                Showing {filteredCount} of {totalCount} items
                {filterDatePreset && filterDatePreset !== "custom" && (
                    <span className="filter-date-active-label">
                        {' '}· {DATE_PRESETS.find(p => p.value === filterDatePreset)?.label}
                    </span>
                )}
                {filterDatePreset === "custom" && filterDateFrom && filterDateTo && (
                    <span className="filter-date-active-label">
                        {' '}· {filterDateFrom} → {filterDateTo}
                    </span>
                )}
            </div>
        </div>
    );
};

export default FilterPanel;
